import type { Dictionary } from '../types'

// Angielski musi mieć dokładnie tę samą strukturę co pl.ts.
// Jeśli dodasz klucz w pl.ts a zapomnisz tu — TypeScript zgłosi błąd kompilacji.
export const en: Dictionary = {
  meta: {
    title: 'Solid Labs PSA | Premium Partner for SMEs',
    description:
      'A technology company from Krakow. We deliver modern digital solutions powered by AI for small and medium-sized enterprises.',
  },
  nav: {
    about: 'About',
    product: 'Product',
    oferta: 'Services',
    realizacje: 'Case studies',
    blog: 'Blog',
    contact: 'Contact',
    spottoApp: 'Spotto App',
    logoAria: 'Solid Labs, Home',
    logoAlt: 'Solid Labs',
    langPlAria: 'Switch to Polish',
    langEnAria: 'English language',
    mobileToggleOpen: 'Open menu',
    mobileToggleClose: 'Close menu',
  },
  hero: {
    title1: 'We build solutions',
    title2: 'for business',
    title3: 'powered by AI',
    subtitle:
      'Solid Labs PSA is a technology company from Krakow. We deliver solutions that digitize business processes, enhanced with the power of artificial intelligence. Our flagship product is Spotto, a platform for the sports industry.',
    ctaPrimary: 'Discover Spotto',
    ctaSecondary: 'About us',
    scrollHint: 'Learn more',
  },
  about: {
    eyebrow: 'About us',
    titlePart1: 'Where technology meets',
    titlePart2: 'business',
    intro:
      'Solid Labs PSA is a technology company from Krakow with international experience. We specialize in digitizing business processes and AI-driven automation, for clients in the most demanding sectors. Customer excellence and long-term, partnership-based relationships are the foundation on which we build every project.',
    facts: {
      operatingSince: {
        title: 'Operating since 2022',
        description:
          'A Krakow-based technology company in the form of a Simple Joint-Stock Company, operating continuously since July 2022.',
      },
      digitizationAi: {
        title: 'Digitization + AI',
        description:
          'Experience in process digitization, data analytics, cloud implementations, and AI-based automation.',
      },
      implementations: {
        title: '12+ implementations delivered',
        description:
          "We've delivered over a dozen projects for business clients, from large corporations to fast-growing companies.",
      },
      crossSector: {
        title: 'Cross-sector experience',
        description:
          'Banking, Finance, TMT, Energy and Retail. We know the specifics and have implemented improvements in complex purchasing and operational processes.',
      },
      solutions: {
        title: 'Solutions delivered',
        description:
          'CRM systems, product catalogs, transactional portals, mobile applications, and process automation powered by data and AI.',
      },
      international: {
        title: 'International experience',
        description:
          "Years of experience in international projects: we've worked with Big 4 firms, large public companies, and other organizations across Poland, Europe, and the USA.",
      },
    },
  },
  privacy: {
    title: 'Privacy Policy',
    closeAria: 'Close',
    lastUpdated: 'Last updated: May 11, 2026',
  },
  oferta: {
    eyebrow: 'Services',
    titlePart1: 'Digitization for',
    titlePart2: 'SMEs',
    mission:
      'Our mission is to provide small and medium-sized enterprises with premium-class digital tools. We simplify, integrate, and deploy modern solutions in a SaaS model, a recurring subscription that optimizes capital expenditure and spreads costs over time.',
    pillars: {
      saas: {
        title: 'SaaS Products',
        description:
          'Ready-to-use SaaS modules that integrate with any existing website without needing a rebuild. Just link them to our system.',
        items: {
          scheduler: {
            title: 'Scheduler',
            desc: 'A complete calendar booking and management module. Ready to embed in any service business.',
          },
          sklep: {
            title: 'PLUGIN COMMERCE',
            desc: 'A sales module with payment gateway, easy integration with any website. Adds an online sales channel with no capital investment.',
          },
        },
      },
      transformacje: {
        title: 'Technology transformations',
        description:
          'We help businesses scale through sales and operations process optimization.',
        items: {
          sales: {
            title: 'Sales optimization',
            desc: 'Activating online channels: fast, affordable, and effective. We connect websites, shops, and marketing automation.',
          },
          operations: {
            title: 'Operations optimization',
            desc: 'Internal process automation: invoicing, document workflows, reporting, system integrations.',
          },
        },
      },
      ai: {
        title: 'AI Automation',
        description:
          'AI implementations where they bring real value, no hype, just measurable ROI.',
        items: {
          analytics: {
            title: 'Data analytics',
            desc: 'We deliver value by understanding data structure, integrating it, and leveraging data insights for better business management.',
          },
          integrations: {
            title: 'AI Integrations',
            desc: 'Connecting language models with existing business systems: CRM, ERP, data warehouses.',
          },
        },
      },
    },
  },
  product: {
    eyebrow: 'Our product',
    description1:
      'A platform that revolutionizes how sports facilities are booked. Find the perfect place to train, reserve it in seconds, and manage your training sessions, all in one app.',
    description2:
      'For facility owners, Spotto is a comprehensive tool for managing reservations, payments, availability calendars, and customer communication. Transparent pricing, no hidden fees.',
    capabilities: {
      search: 'Find nearby venues',
      booking: 'Real-time booking',
      payments: 'Secure online payments',
      trainers: 'Find personal trainers',
      mobile: 'iOS and Android mobile app',
      panel: 'Management panel for owners',
    },
    ctaVisit: 'Visit product website',
    screens: {
      home: 'Home screen',
      explore: 'Explore venues',
      booking: 'Book a slot',
      training: 'Training signup',
      shop: 'Shop',
    },
    carouselAria: 'Select app screen',
  },
  realizacje: {
    eyebrow: 'Case studies',
    title: "What we've built",
    subtitle:
      'Products and systems we have built and keep developing: a booking platform, an online sales module, and the AI assistant that runs our own sales.',
    projects: {
      spotto: {
        category: 'Sports platform',
        title: 'Spotto',
        description:
          'Mobile app and web platform for sports facility booking, training management, and connecting athletes with trainers.',
      },
      pluginCommerce: {
        category: 'Sales module',
        title: 'PLUGIN COMMERCE',
        description:
          'A ready-made module that switches on online sales of services and products on an existing website in minutes. No capital outlay on building a system, only a commission charged on each sale.',
      },
      asystent: {
        category: 'Sales and operations assistant',
        title: 'Hermes',
        description:
          'An agent that reads the inbox, turns dictated notes into structured client records, and guards the deadlines you promised. It runs our own sales, so we describe it together with the mistakes it made along the way.',
      },
    },
  },
  blog: {
    meta: {
      title: 'Blog | Solid Labs PSA',
      description:
        'Field notes from real projects: process digitization, AI-supported automation and building SaaS products for small and mid-sized companies.',
    },
    eyebrow: 'Blog',
    titlePart1: 'What we learn',
    titlePart2: 'shipping software',
    subtitle:
      'Notes from real projects: process digitization, AI automation and building Spotto. No hype, just what worked and what did not.',
    allCategories: 'All',
    filterAria: 'Filter articles by category',
    readingTime: 'min read',
    empty: 'The first articles will appear here soon.',
    backToList: 'All articles',
    moreArticles: 'Keep reading',
    readArticle: 'Read article',
    featured: 'Latest',
    translationNotice:
      'This article is not available in English yet, showing the Polish original.',
    notFound: {
      title: 'We could not find this article',
      description:
        'The link may be outdated or the article has moved. Have a look at the full list instead.',
      cta: 'Back to blog',
    },
    articleCta: {
      title: 'Does this sound like your company?',
      subtitle:
        'If you are facing a similar problem, get in touch. The first conversation costs nothing.',
      button: 'Get in touch',
    },
  },
  cta: {
    titlePart1: 'Ready for',
    titlePart2: 'collaboration?',
    subtitle:
      'Whether you run a sports business, are looking for a technology partner, or want to learn more about Spotto, get in touch.',
    ctaPrimary: 'Get in touch',
    ctaSecondary: 'Visit Spotto',
  },
  footer: {
    descriptionLine1: 'A technology company from Krakow,',
    descriptionLine2: 'Premium Partner for Small and Medium-sized Enterprises.',
    legalForm: 'Simple Joint-Stock Company',
    productHeader: 'Product',
    companyHeader: 'Company',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
  },
}
